import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Plus, Users, BookOpen, Trash2, Eye, Link as LinkIcon, Copy, Loader2 } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPrograms() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEnrollments, setShowEnrollments] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState({
    title: '', slug: '', description: '', short_description: '',
    program_type: 'group_coaching', schedule: '', duration: '',
    capacity: '', price: '', features: '', start_date: ''
  });

  useEffect(() => { fetchPrograms(); }, []);

  const fetchPrograms = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/programs`, { withCredentials: true });
      setPrograms(res.data);
    } catch { toast.error('Failed to load programs'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/programs`, {
        ...form,
        slug: form.slug || form.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        capacity: form.capacity ? parseInt(form.capacity) : null,
        features: form.features ? form.features.split('\n').filter(f => f.trim()) : []
      }, { withCredentials: true });
      toast.success('Program created');
      setShowCreate(false);
      setForm({ title: '', slug: '', description: '', short_description: '', program_type: 'group_coaching', schedule: '', duration: '', capacity: '', price: '', features: '', start_date: '' });
      fetchPrograms();
    } catch { toast.error('Failed to create program'); }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('Delete this program?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/programs/${programId}`, { withCredentials: true });
      toast.success('Program deleted');
      fetchPrograms();
    } catch { toast.error('Failed to delete program'); }
  };

  const viewEnrollments = async (programId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/programs/${programId}/enrollments`, { withCredentials: true });
      setEnrollments(res.data);
      setShowEnrollments(programId);
    } catch { toast.error('Failed to load enrollments'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const [linkBusy, setLinkBusy] = useState(null);
  const [paymentLinks, setPaymentLinks] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/payments/payment-links`, { withCredentials: true })
      .then((r) => {
        const map = {};
        (r.data || []).forEach((l) => {
          if (l.active && l.item_type === 'program') map[l.item_id] = l;
        });
        setPaymentLinks(map);
      })
      .catch(() => {});
  }, []);

  const generatePayLink = async (programId) => {
    setLinkBusy(programId);
    try {
      const res = await axios.post(`${API_URL}/api/admin/payments/payment-link`, {
        item_type: 'program',
        item_id: programId,
      }, { withCredentials: true });
      setPaymentLinks((m) => ({ ...m, [programId]: res.data }));
      try { await navigator.clipboard.writeText(res.data.url); } catch { /* ignore */ }
      toast.success('Pay link generated and copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate pay link');
    } finally {
      setLinkBusy(null);
    }
  };

  const copyPayLink = async (programId) => {
    const link = paymentLinks[programId];
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success('Pay link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <AdminLayout title="Programs"><div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0A0F1C]" data-testid="programs-admin-title">Programs ({programs.length})</h1>
          <Button onClick={() => setShowCreate(true)} className="btn-primary-blue flex items-center gap-2" data-testid="create-program-btn">
            <Plus size={16} /> Create Program
          </Button>
        </div>

        <div className="space-y-4">
          {programs.map((p, i) => (
            <div key={p.program_id} className="bg-white p-6 border border-[#E2E8F0] rounded-lg" data-testid={`admin-program-${i}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-[#0A0F1C]">{p.title}</h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]">{p.program_type?.replace(/_/g, ' ')}</span>
                    {!p.is_active && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Inactive</span>}
                  </div>
                  <p className="text-sm text-[#475569]">{p.short_description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#94A3B8]">
                    {p.price && <span>{p.price}</span>}
                    {p.duration && <span>{p.duration}</span>}
                    <span className="flex items-center gap-1"><Users size={12} />{p.enrolled_count || 0} enrolled{p.capacity ? `/${p.capacity}` : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {paymentLinks[p.program_id] ? (
                    <Button onClick={() => copyPayLink(p.program_id)} className="btn-outline text-sm flex items-center gap-1" data-testid={`copy-paylink-${i}`}>
                      <Copy size={14} /> Copy Pay Link
                    </Button>
                  ) : (
                    <Button onClick={() => generatePayLink(p.program_id)} disabled={linkBusy === p.program_id} className="btn-outline text-sm flex items-center gap-1" data-testid={`gen-paylink-${i}`}>
                      {linkBusy === p.program_id ? <Loader2 size={14} className="animate-spin" /> : <LinkIcon size={14} />} Pay Link
                    </Button>
                  )}
                  <Button onClick={() => viewEnrollments(p.program_id)} className="btn-outline text-sm flex items-center gap-1" data-testid={`view-enrollments-${i}`}><Eye size={14} />Enrollments</Button>
                  <Button onClick={() => handleDelete(p.program_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`delete-program-${i}`}><Trash2 size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-[#0A0F1C] mb-4">Create Program</h3>
              <form onSubmit={handleCreate} className="space-y-3">
                <Input placeholder="Program Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="program-title-input" />
                <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required />
                <Input placeholder="Short Description" value={form.short_description} onChange={(e) => setForm({...form, short_description: e.target.value})} required />
                <select value={form.program_type} onChange={(e) => setForm({...form, program_type: e.target.value})} className="form-input w-full">
                  <option value="group_coaching">Group Coaching</option>
                  <option value="bootcamp">Bootcamp</option>
                  <option value="course">Course</option>
                  <option value="workshop">Workshop</option>
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Schedule" value={form.schedule} onChange={(e) => setForm({...form, schedule: e.target.value})} />
                  <Input placeholder="Duration" value={form.duration} onChange={(e) => setForm({...form, duration: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Capacity" type="number" value={form.capacity} onChange={(e) => setForm({...form, capacity: e.target.value})} />
                  <Input placeholder="Price" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} />
                </div>
                <Input type="date" placeholder="Start Date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} />
                <Textarea placeholder="Features (one per line)" value={form.features} onChange={(e) => setForm({...form, features: e.target.value})} className="min-h-[80px]" />
                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={() => setShowCreate(false)} className="btn-outline flex-1">Cancel</Button>
                  <Button type="submit" className="btn-primary-blue flex-1">Create</Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Enrollments Modal */}
        {showEnrollments && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0A0F1C]">Enrollments ({enrollments.length})</h3>
                <button onClick={() => setShowEnrollments(null)} className="text-[#475569]">&times;</button>
              </div>
              {enrollments.length > 0 ? (
                <div className="space-y-3">
                  {enrollments.map((e, i) => (
                    <div key={e.enrollment_id} className="p-3 border border-[#E2E8F0] rounded" data-testid={`enrollment-${i}`}>
                      <div className="font-medium text-[#0A0F1C]">{e.name}</div>
                      <div className="text-sm text-[#475569]">{e.email}</div>
                      <div className="text-xs text-[#94A3B8]">Enrolled: {new Date(e.enrolled_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-[#475569] text-center py-4">No enrollments yet</p>}
            </div>
          </div>
        )}
      </div></AdminLayout>);}
